import SignInWithPopup from './auth';
import { googleProvider, appleProvider, auth, firebase } from '../config/firebase';
import { toast } from 'react-hot-toast';
import { saveUser, checkUserPhoneVerified } from '../api/users/saveUsers';
import Cookies from 'js-cookie';
import {
  parseAccountExistsError,
  stashPendingLink,
  getPendingLink,
  clearPendingLink,
} from './accountLinking';

const credentialFromAuthError = (error, newProvider) => {
  try {
    if (newProvider === 'apple.com') {
      return firebase.auth.OAuthProvider.credentialFromError(error);
    }
    return (
      firebase.auth.GoogleAuthProvider.credentialFromError(error) ||
      firebase.auth.OAuthProvider.credentialFromError(error)
    );
  } catch (e) {
    return null;
  }
};

const asNeedsLink = (error, newProvider) => {
  const parsed = parseAccountExistsError(error);
  if (!parsed) return null;
  stashPendingLink({
    email: parsed.email,
    credential: credentialFromAuthError(error, newProvider),
    newProvider,
  });
  return { needsLink: true, email: parsed.email, newProvider };
};

const finishSocialSession = async (authUser, loginType) => {
  const payload = {
    email: authUser.email,
    displayName: authUser.displayName || authUser.email?.split('@')[0] || 'User',
    uid: authUser.uid,
    emailVerified: authUser.emailVerified ?? true,
  };

  let existingUser = null;
  try {
    existingUser = await checkUserPhoneVerified(authUser.uid);
  } catch (e) {
    console.warn('Check phone verified failed:', e);
  }

  let response;
  try {
    response = await saveUser({ ...payload, loginType });
  } catch (err) {
    console.error('Save user error:', err);
    toast.error(
      err.message ||
        'Could not save account. Is the backend running at ' +
          (process.env.REACT_APP_BASE_URL || 'REACT_APP_BASE_URL') +
          '?'
    );
    return null;
  }

  const user = response?.user;
  if (!user || !user.uid) {
    toast.error('Invalid response from server');
    return null;
  }

  Cookies.set('uid', user.uid);
  Cookies.set('email', user.email || authUser.email);
  if (user.customerStripeID) Cookies.set('customerStripeID', user.customerStripeID);

  if (process.env.REACT_APP_SKIP_PHONE_VERIFICATION === 'true') {
    toast.success('Signed in (phone verification skipped for dev).');
    return { ...user, phoneVerified: true };
  }
  if (existingUser && existingUser.phoneVerified) {
    toast.success('Welcome back!');
    return { ...user, phoneVerified: true };
  }
  toast.success('Signed in. Please verify your phone.');
  return { ...user, phoneVerified: false };
};

/**
 * Google OAuth Authentication
 * Returns user data for phone verification flow, or `{ needsLink }` when
 * this email already belongs to an Apple / password account.
 */
const googleAuth = async () => {
  try {
    const res = await SignInWithPopup(googleProvider);
    if (!res || (!res.emailVerified && !res.email)) {
      toast.error('Google sign-in was cancelled');
      return null;
    }
    return await finishSocialSession(res, 'google.com');
  } catch (error) {
    console.error('Google auth error:', error);
    const link = asNeedsLink(error, 'google.com');
    if (link) return link;
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      toast.error('Sign-in was cancelled');
    } else {
      toast.error(error.message || 'Google sign-in failed');
    }
    return null;
  }
};

/**
 * Apple Sign-In Authentication
 * Returns user data for phone verification flow, or `{ needsLink }` when
 * this email already belongs to a Google / password account.
 */
const appleAuth = async () => {
  try {
    const result = await auth.signInWithPopup(appleProvider);
    const user = result.user;
    const displayName =
      user.displayName ||
      (result._tokenResponse?.firstName && result._tokenResponse?.lastName
        ? `${result._tokenResponse.firstName} ${result._tokenResponse.lastName}`
        : 'Apple User');
    if (!user.displayName && displayName) {
      try {
        await user.updateProfile({ displayName });
      } catch (e) {
        // Non-fatal — finishSocialSession falls back to email local-part.
      }
    }
    return await finishSocialSession(user, 'apple.com');
  } catch (error) {
    console.error('Apple auth error:', error);
    const link = asNeedsLink(error, 'apple.com');
    if (link) return link;
    if (error.code === 'auth/popup-closed-by-user') {
      toast.error('Sign-in was cancelled');
    } else {
      toast.error(error.message || 'Apple sign-in failed');
    }
    return null;
  }
};

const updateUserPhone = async (uid, phoneNumber) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/update-phone`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uid, phoneNumber, phoneVerified: true }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update phone number');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Update phone error:', error);
    throw error;
  }
};

/**
 * Logout user
 */
const logout = async () => {
  try {
    await auth.signOut();
    Cookies.remove('email');
    Cookies.remove('uid');
    Cookies.remove('customerStripeID');
    Cookies.remove('phoneVerified');
    localStorage.clear();
    toast.success('Logged out successfully');
  } catch (error) {
    toast.error(error.message || 'Logout failed');
    console.error('Logout error:', error);
  }
};

/**
 * Check if current user's session is valid
 */
const checkAuthState = () => {
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

/**
 * After the member signs in with the method they already use, attach the
 * pending Google/Apple credential so both methods share one Firebase uid.
 */
const completeAccountLink = async ({ via, email, password } = {}) => {
  const pending = getPendingLink();
  if (!pending) {
    toast.error('No pending sign-in to connect. Please try again.');
    return null;
  }

  try {
    let user;
    if (via === 'google') {
      const result = await auth.signInWithPopup(googleProvider);
      user = result.user;
    } else if (via === 'apple') {
      const result = await auth.signInWithPopup(appleProvider);
      user = result.user;
    } else if (via === 'password') {
      const result = await auth.signInWithEmailAndPassword(
        email || pending.email,
        password
      );
      user = result.user;
    } else {
      toast.error('Unknown sign-in method');
      return null;
    }

    if (pending.credential) {
      try {
        await user.linkWithCredential(pending.credential);
      } catch (err) {
        if (err.code !== 'auth/provider-already-linked') {
          throw err;
        }
      }
    }

    clearPendingLink();
    const loginType = user.providerData?.[0]?.providerId || via || 'google.com';
    return await finishSocialSession(user, loginType);
  } catch (error) {
    console.error('Complete account link error:', error);
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      toast.error('Sign-in was cancelled');
    } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      toast.error('Invalid email or password');
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      toast.error('That method belongs to a different account. Try another.');
    } else {
      toast.error(error.message || 'Could not connect this sign-in');
    }
    return null;
  }
};

/**
 * Link additional provider to existing account
 */
const linkAccount = async (credential) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user logged in');
    }

    const result = await user.linkWithCredential(credential);
    toast.success('Account linked successfully!');
    return result.user;
  } catch (error) {
    console.error('Link account error:', error);
    toast.error(error.message || 'Failed to link account');
    return null;
  }
};

export {
  googleAuth,
  appleAuth,
  updateUserPhone,
  logout,
  checkAuthState,
  linkAccount,
  completeAccountLink,
};
